import BasePageLayout from '../../components/BasePageLayout';
import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../context/UserContext';
import { useNavigate } from 'react-router-dom';
import useSnackbar from '../../hooks/useSnackbar';
import { validateUsername } from '../../services/utils';
import { useForm } from '../../hooks/useForm';
import axios from 'axios';
import api, { API_URL, jwtTokenKey } from '../../services/api';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    Input,
    TextField,
    Typography
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import LogoutIcon from '@mui/icons-material/Logout';
import { readAndCompressImage } from 'browser-image-resizer';

const SettingsPage = () => {
    const {user, avatarUrl, updateUser} = useContext(UserContext);
    const [updating, setUpdating] = useState<boolean>(false);
    const [uploadingAvatar, setUploadingAvatar] = useState<boolean>(false);
    const [openLogoutDialog, setOpenLogoutDialog] = useState<boolean>(false);
    const navigate = useNavigate();
    const {showSnackbar, SnackbarComponent} = useSnackbar();

    const initialFormValues = {
        username: '',
        avatar: undefined as number | undefined,
    };

    const validate = (values: typeof initialFormValues) => ({
        username: values.username.length >= 3
    });

    const {
        formData,
        formTouched,
        isFormValid,
        handleInputChange,
        handleBlur,
        setFormData,
    } = useForm({
        initialValues: initialFormValues,
        validate,
    });

    useEffect(() => {
        if (user) {
            setFormData({
                username: user.username || '',
                avatar: user.avatar?.id,
            });
        }
    }, [user, setFormData]);

    const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) {
            showSnackbar('No file selected', 'error');
            return;
        }

        const config = {
            quality: 1,
            maxWidth: 800,
            maxHeight: 800,
            autoRotate: true,
        };

        try {
            const correctedFile = await readAndCompressImage(file, config);

            const formData = new FormData();
            formData.append('files', correctedFile, file.name);
            formData.append('ref', 'plugin::users-permissions.user');
            formData.append('refId', String(user?.id));
            formData.append('field', 'avatar');

            setUploadingAvatar(true);

            const response = await axios.post<{ id: number; url: string }[]>(
                `${API_URL}/api/upload`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem(jwtTokenKey)}`,
                    },
                }
            );

            const uploadedFile = response.data[0];
            setFormData((prevState) => ({
                ...prevState,
                avatar: uploadedFile.id,
            }));

            updateUser({
                avatar: {
                    id: uploadedFile.id,
                    url: uploadedFile.url,
                },
            });

            showSnackbar('Profile picture uploaded successfully', 'success');
        } catch (error) {
            showSnackbar('Failed to upload profile picture', 'error');
            console.error('Error uploading profile picture:', error);
        } finally {
            setUploadingAvatar(false);
        }
    };

    const handleUpdateProfile = async () => {
        if (!user?.id) {
            showSnackbar('User ID is not available', 'error');
            return;
        }

        setUpdating(true);
        try {
            const updatedData = {
                username: formData.username,
                avatar: formData.avatar as any,
            };

            await api.put(`/users/${user.id}`, updatedData);

            updateUser(updatedData);

            showSnackbar('Profile updated successfully', 'success');
            navigate('/merchant/home');
        } catch (error) {
            showSnackbar('Failed to update profile', 'error');
            console.error('Error updating profile:', error);
        } finally {
            setUpdating(false);
        }
    };

    const handleLogout = () => {
        setOpenLogoutDialog(true);
    };

    const handleConfirmLogout = () => {
        setOpenLogoutDialog(false);
        showSnackbar('Logged out successfully', 'success');
        localStorage.removeItem(jwtTokenKey);
        updateUser(null);
        navigate('/login');
    };

    const handleCancelLogout = () => {
        setOpenLogoutDialog(false);
    };

    return (
        <BasePageLayout title="Edit Profile" showNotifications={false}>
            <Box component="form" width="100%">
                <>
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        mb={3}
                        mt={1}
                        ml={uploadingAvatar ? 0 : 6}
                        position="relative"
                    >
                        {uploadingAvatar ? (
                            <Box sx={{height: '120px', display: 'flex', alignItems: 'center'}}>
                                <CircularProgress/>
                            </Box>
                        ) : (
                            <>
                                <Avatar
                                    src={avatarUrl || ''}
                                    sx={{
                                        width: 170,
                                        height: 170,
                                    }}
                                />

                                <label htmlFor="icon-button-file">
                                    <Input
                                        id="icon-button-file"
                                        type="file"
                                        inputProps={{accept: "image/*"}}
                                        sx={{display: 'none', position: 'absolute'}}
                                        onChange={handleAvatarUpload}
                                    />
                                    <IconButton
                                        color="secondary"
                                        aria-label="upload picture"
                                        component="span"
                                        sx={{
                                            position: 'relative',
                                            bottom: -57,
                                            left: -40,
                                            backgroundColor: 'transparent ',
                                            borderRadius: '50%',
                                            padding: '12px',
                                        }}
                                    >
                                        <PhotoCamera style={{color: '#FF8C00', fontSize: 24}}/>
                                    </IconButton>
                                </label>
                            </>
                        )}
                    </Box>

                    <FormControl fullWidth sx={{marginBottom: '20px'}}>
                        <Typography fontSize={16}>Username</Typography>
                        <TextField
                            id="username"
                            value={formData.username}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            error={!validateUsername(formData.username) && formTouched.username}
                            helperText={
                                !validateUsername(formData.username) && formTouched.username
                                    ? 'Username must be at least 3 characters long'
                                    : ''
                            }
                        />
                    </FormControl>

                    <Typography fontSize={16} mb={2}>Phone Number</Typography>
                    <Typography color='rgba(0, 0, 0, 0.5)'>{user?.phoneNumber}</Typography>

                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleUpdateProfile}
                        disabled={!isFormValid || updating}
                        sx={{mb: 2, mt: 5}}
                    >
                        {updating ? <CircularProgress size={24}/> : 'Update Profile'}
                    </Button>

                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleLogout}
                        startIcon={<LogoutIcon/>}
                        sx={{
                            backgroundColor: 'white',
                            color: 'rgba(57, 57, 67, 1)',
                            fontWeight: 600,
                            border: '1px solid rgba(57, 57, 67, 1)',
                            '&:hover': {
                                backgroundColor: 'rgba(57, 57, 67, 0.05)',
                            },
                            mb: 2,
                        }}
                    >
                        Log Out
                    </Button>

                    <Dialog
                        open={openLogoutDialog}
                        onClose={handleCancelLogout}
                        PaperProps={{
                            sx: {
                                borderRadius: '16px',
                                padding: '24px',
                                textAlign: 'center',
                                minWidth: '320px',
                            },
                        }}
                    >
                        <DialogTitle>
                            <Typography fontWeight="bold" fontSize="24px">
                                Are you sure?
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <Box
                                component="div"
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mb: 2,
                                }}
                            >
                                <Button
                                    variant="contained"
                                    fullWidth
                                    onClick={handleConfirmLogout}
                                    sx={{
                                        backgroundColor: '#2E2E36',
                                        color: '#fff',
                                        fontWeight: 'medium',
                                        height: '48px',
                                        borderRadius: '12px',
                                        marginBottom: '12px',
                                        '&:hover': {
                                            backgroundColor: '#26262e',
                                        },
                                    }}
                                >
                                    Log Out
                                </Button>
                            </Box>
                            <Button
                                fullWidth
                                onClick={handleCancelLogout}
                                sx={{
                                    backgroundColor: 'white',
                                    fontWeight: 'bold',
                                    color: '#2E2E36',
                                    height: '48px',
                                    borderRadius: '12px',
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                    },
                                }}
                            >
                                Cancel
                            </Button>
                        </DialogContent>
                    </Dialog>
                </>
                <SnackbarComponent/>
            </Box>
        </BasePageLayout>
    );
};

export default SettingsPage;